import { Component, inject, OnInit, signal } from '@angular/core';
import { NavigationEnd, RouterOutlet, Router } from '@angular/router';
import { first } from 'rxjs';
import { WebSocketService } from './common/services/websocket.service';
import { UserStatusService } from './common/services/user-status.service';
import { LoginService } from './pages/login/login.service';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { NotificationService } from './pages/notification/notification.service';
import { MediaUploadService } from './common/services/media-upload.service';
import { CookieStorageService } from './common/helpers/cookie-storage.service';
import { IUser } from './common/interfaces/user.interface';
import { environment } from '../environments/environment';
import { AdminImpersonationBannerComponent } from './components/admin-impersonation-banner/admin-impersonation-banner.component';
import { MigrationNoticeModalComponent } from './components/migration-notice-modal/migration-notice-modal.component';
import { MigrationNoticeService } from './common/services/migration-notice.service';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [RouterOutlet, AdminImpersonationBannerComponent, MigrationNoticeModalComponent],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
    webSocketService = inject(WebSocketService);
    private readonly userStatusService = inject(UserStatusService);
    private readonly loginService = inject(LoginService);
    private readonly gtmService = inject(GoogleTagManagerService);
    private readonly router = inject(Router);
    private readonly notificationService = inject(NotificationService);
    private readonly mediaUploadService = inject(MediaUploadService);
    private readonly cookieService = inject(CookieStorageService);
    private readonly migrationNoticeService = inject(MigrationNoticeService);
    
    isAccessGranted = true;
    showMigrationNotice = signal(false);

    ngOnInit() {
        // Capture promo code from marketing URL before anything else
        this.capturePromoCode();

        // Check access gate for dev/staging environments
        if (!this.checkAccessGate()) {
            this.isAccessGranted = false;
            return;
        }
        
        console.log('AppComponent ngOnInit called');
        this.webSocketService.connect();
        
        // Start user status updates if logged in
        const isLoggedIn = this.loginService.isLoggedIn();
        console.log('Is user logged in:', isLoggedIn);
        
        if (isLoggedIn) {
            this.loginService.setSharedLoginCookie();
            this.userStatusService.startStatusUpdates();
            this.initializeNotifications();
            this.showMigrationNoticeIfNeeded();
        }

        this.router.events.forEach((item) => {
            if (item instanceof NavigationEnd) {
                const gtmTag = {
                    event: 'page',
                    pageName: item.url
                };
                this.gtmService.pushTag(gtmTag);
                this.showMigrationNoticeIfNeeded(item.urlAfterRedirects);
            }
        });
    }

    onMigrationNoticeAcknowledge(): void {
        const userInfo = this.cookieService.get<IUser>('userInfo');
        this.migrationNoticeService.markAsSeen(userInfo);
        this.showMigrationNotice.set(false);
    }

    private showMigrationNoticeIfNeeded(currentUrl = this.router.url): void {
        if (!this.loginService.isLoggedIn()) {
            this.showMigrationNotice.set(false);
            return;
        }

        if (this.showMigrationNotice()) {
            return;
        }

        const userInfo = this.cookieService.get<IUser>('userInfo');
        if (this.migrationNoticeService.shouldShow(userInfo, currentUrl)) {
            this.showMigrationNotice.set(true);
        }
    }

    private initializeNotifications(): void {
        console.log('initializeNotifications called');
        
        // Get user ID from cookie
        const userInfo = this.cookieService.get<IUser>('userInfo');
        console.log('User info from cookie:', userInfo);
        
        // Immediately restore notification state from cookie for faster display
        const savedNotificationStatus = this.cookieService.get<boolean>('hasUnreadNotifications');
        console.log('Saved notification status from cookie:', savedNotificationStatus);
        if (savedNotificationStatus !== null) {
            this.mediaUploadService.setNotificationCount(savedNotificationStatus);
        }
        
        // Wait for WebSocket connection before joining notification channel
        this.webSocketService.onConnected().pipe(first()).subscribe(() => {
            if (userInfo?.id) {
                this.webSocketService.joinNotificationChannel(userInfo.id);
                console.log('Joined notification channel for user:', userInfo.id);
            }
        });

        // Listen for real-time notification updates
        this.webSocketService.listenForNotifications().subscribe((data) => {
            console.log('Notification received:', data);
            this.mediaUploadService.setNotificationCount(data.hasUnreadNotifications);
            // Update cookie to persist state
            this.cookieService.set('hasUnreadNotifications', data.hasUnreadNotifications);
        });

        // Fetch initial unread notification status from API (authoritative source)
        this.notificationService.getUnreadStatus().subscribe({
            next: (response) => {
                console.log('Unread status response:', response);
                if (response?.data) {
                    this.mediaUploadService.setNotificationCount(response.data.hasUnreadNotifications);
                    this.cookieService.set('hasUnreadNotifications', response.data.hasUnreadNotifications);
                }
            },
            error: (err) => {
                console.error('Failed to fetch notification status:', err);
            }
        });
    }

    /**
     * Capture coupon code from marketing URL (e.g., creativehubinternational.com/?coupon=SUMMER25).
     * Uses window.location.search directly for reliability at root level.
     * Stores the code in a cookie and removes the query param from the URL.
     */
    private capturePromoCode(): void {
        const urlParams = new URLSearchParams(window.location.search);
        const couponCode = urlParams.get('coupon');
        if (couponCode) {
            this.cookieService.set('promoCode', couponCode.toUpperCase());
            // Remove the coupon query param from URL cleanly
            urlParams.delete('coupon');
            const cleanPath = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
            window.history.replaceState({}, '', cleanPath);
        }
    }

    private checkAccessGate(): boolean {
        // Skip if access code is not required
        if (!environment.requireAccessCode) {
            return true;
        }

        const ACCESS_KEY = 'devAccessTimestamp';
        const ONE_DAY_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

        // Check if access was previously granted and not expired
        const storedTimestamp = localStorage.getItem(ACCESS_KEY);
        if (storedTimestamp) {
            const timestamp = parseInt(storedTimestamp, 10);
            const now = Date.now();
            if (now - timestamp < ONE_DAY_MS) {
                return true; // Access still valid
            }
            // Expired, remove the old timestamp
            localStorage.removeItem(ACCESS_KEY);
        }

        // Prompt for access code
        const enteredCode = prompt('Enter access code to view this site:');
        if (enteredCode === environment.accessCode) {
            localStorage.setItem(ACCESS_KEY, Date.now().toString());
            return true;
        }

        // Access denied
        alert('Invalid access code. Access denied.');
        return false;
    }

    retryAccess(): void {
        if (this.checkAccessGate()) {
            this.isAccessGranted = true;
            this.ngOnInit();
        }
    }
}
